// 只在客户端导入 fe2app-sdk，避免 SSR 报错
if (import.meta.client) {
	import("@admin/fe2app-sdk");
}

import { STORAGE_CHANNEL_KEY, STORAGE_TOKEN_KEY } from "~/utils/constants";
import { setHeaders } from "~/composables/useRequest";
import queryString from "query-string";
import { defaultEnvOptions } from "./init.js";
import { DownloadUrl } from "~/utils/common";
import { useClipboard } from "@vueuse/core";
import { parseUA } from "./browser.js";

/**
 * 同步判断是否在 app 内（可能不准确，如果 SDK 还未加载）
 */
export function isInApp() {
	if (!import.meta.client) return false;

	// 检查 window.mmc 是否存在
	if (typeof window === "undefined" || !window.mmc) {
		return false;
	}

	// 检查必要的属性是否存在
	if (!window.mmc.client || typeof window.mmc.client.isAPP === "undefined") {
		return false;
	}

	// 如果 isAPP 为 false，直接返回 false
	if (!window.mmc.client.isAPP) {
		return false;
	}

	// 检查是否在指定的 app 内
	const app = ["ljds", "ydk"];
	if (typeof window.mmc.client.is !== "function") {
		return false;
	}

	return app.some(val => window.mmc.client.is(val));
}

/**
 * 异步判断是否在 app 内（等待 SDK 加载完成）
 * @param {number} maxWaitTime 最大等待时间（毫秒），默认 2000ms
 * @returns {Promise<boolean>}
 */
export async function isInAppAsync(maxWaitTime = 2000) {
	if (!import.meta.client) return false;

	const startTime = Date.now();

	// 轮询检查 window.mmc 是否已加载
	while (Date.now() - startTime < maxWaitTime) {
		if (typeof window !== "undefined" && window.mmc) {
			// 检查必要的属性是否存在
			if (window.mmc.client && typeof window.mmc.client.isAPP !== "undefined") {
				// 如果 isAPP 为 false，直接返回 false
				if (!window.mmc.client.isAPP) {
					return false;
				}

				// 检查是否在指定的 app 内
				const app = ["ljds", "ydk"];
				if (typeof window.mmc.client.is === "function") {
					return app.some(val => window.mmc.client.is(val));
				}
			}
		}

		// 等待 50ms 后再次检查
		await new Promise(resolve => setTimeout(resolve, 50));
	}

	// 超时后使用同步方法再试一次
	return isInApp();
}

export const isApp = /*#__PURE__*/ isInApp();

export function goAppModule({ action = 110, global_type, func_type, data, callBack, config }) {
	if (!import.meta.client || !window.mmc || !window.mmc.client || !window.mmc.client.goto) {
		if (callBack && typeof callBack === "function") {
			callBack();
		}
		return;
	}

	const { closeWindow, needRefresh, needCallback } = Object.assign(
		{
			closeWindow: false,
			needRefresh: true,
			needCallback: true,
		},
		config
	);

	// todo window.mmc.client.goto的callBack测试过不执行，改成这种偶尔执行。。。。
	// window.MMCWKEventWeb.eventController.addEvent('cb_MMCGoto', (params) => {
	//   callBack(params);
	// });

	window.mmc.client.goto(
		{
			params: {
				action,
				actioncontent: {
					global_type: global_type,
					func_type: func_type,
					data: data || {},
				},
				closeWindow,
				needRefresh,
				needCallback,
			},
		},
		callBack
		// 'cb_MMCGoto',
	);

	// todo 上面的callBack有时候不触发，暂时解决方案
	setTimeout(() => {
		if (callBack && typeof callBack === "function") {
			callBack();
		}
	}, 1500);
}

/**
 * 在app内打开外部浏览器
 * @param link
 */
export function openExternalBrowser(link) {
	goAppModule({
		action: 103,
		global_type: link,
	});
}

export function getAppUserInfo() {
	if (!import.meta.client || !window.mmc || !window.mmc.user) return {};
	return window.mmc.user.getInfo() || {};
}

export function getAppDeviceInfo() {
	if (!import.meta.client || !window.mmc || !window.mmc.client) return {};
	return window.mmc.client.getInfo() || {};
}

export async function appPromiseInit() {
	if (!import.meta.client || !window.mmc) {
		return Promise.resolve();
	}

	if (window.mmc.afterReady) return Promise.resolve();

	if (!window.mmc.afterReady) {
		window.mmc.init();
	}

	return new Promise(resolve => {
		if (window.mmc.ready) {
			window.mmc.ready(resolve);
		} else {
			resolve();
		}
	});
}

/**
 * 获取用户信息，支持重试机制
 * @param {number} maxRetries 最大重试次数
 * @param {number} retryDelay 重试延迟时间(ms)
 * @returns {Promise<Object>} 用户信息
 */
async function getUserInfoWithRetry(maxRetries = 10, retryDelay = 500) {
	for (let i = 0; i < maxRetries; i++) {
		try {
			const userInfo = getAppUserInfo();

			// 如果获取到token，直接返回
			if (userInfo.token) {
				return userInfo;
			}

			// 如果没有token且不是最后一次重试，等待后重试
			if (i < maxRetries - 1) {
				await new Promise(resolve => setTimeout(resolve, retryDelay));
				// 每次重试延迟时间递增
				retryDelay *= 1.5;
			}
		} catch (error) {
			if (i < maxRetries - 1) {
				await new Promise(resolve => setTimeout(resolve, retryDelay));
				retryDelay *= 1.5;
			}
		}
	}

	// 所有重试都失败，返回空对象
	return {};
}

export async function appEnvInit(options) {
	if (!import.meta.client) return;

	options = {
		...defaultEnvOptions,
		...options,
	};

	await appPromiseInit();

	// 使用重试机制获取用户信息
	const userInfo = await getUserInfoWithRetry();
	const deviceInfo = getAppDeviceInfo();

	// 只有在 app 内才执行这些操作
	if (isInApp() && window.mmc && window.mmc.client) {
		goAppModule({
			global_type: "web",
			func_type: options.appTitleBar ? "set_normal_titlebar" : "set_transparent_titlebar",
		});

		if (!userInfo.token) {
			if (window.mmc.user && window.mmc.user.login) {
				window.mmc.user.login(() => {
					location.reload();
				});
			}
			return;
		}
	}

	// 设置请求头
	if (deviceInfo.lang) {
		setHeaders("Lang", deviceInfo.lang);
	}
	setHeaders("system", "app_web");

	// app内channel参数获取的优先级：路由query > 客户端 > 项目默认
	const query = queryString.parse(location.search);
	const config = useRuntimeConfig();
	const defaultChannel = config.public.defaultChannel || "seer_kqcs_00001";
	const channel = query.channel || userInfo.channel || defaultChannel;

	if (channel) {
		localStorage.setItem(STORAGE_CHANNEL_KEY, channel);
	}

	// Token
	if (userInfo.token) {
		localStorage.setItem(STORAGE_TOKEN_KEY, userInfo.token);
	}

	const platform = options.platform || userInfo.platform || "web";

	// version
	if (userInfo.version) {
		localStorage.setItem("app_version", userInfo.version);
	}
}

export function callNativeFn(name, data) {
	if (!import.meta.client || !window.mmc || !window.mmc.callClientFunction) {
		return Promise.resolve(null);
	}

	return new Promise(resolve => {
		window.mmc.callClientFunction(name, data, cb_data => {
			if (!cb_data) {
				resolve(cb_data);
				return;
			}

			try {
				resolve(JSON.parse(cb_data));
			} catch (e) {
				// 不确定客户端是不是返回的json，后续有需要就改改
				console.error(e);
				resolve(e);
			}
		});
	});
}

export const downloadApp = (type = null) => {
	let isIos;
	if (type) {
		isIos = Boolean(type === "ios");
	} else {
		const res = parseUA();
		isIos = res.ios;
	}

	if (isIos) {
		window.location.href = DownloadUrl.ios;
	} else {
		window.location.href = DownloadUrl.android;
	}
};

export function openApp(type) {
	let copyToken;
	if (localStorage.getItem("token")) {
		const { copy } = useClipboard({
			legacy: true,
		});
		copyToken = `SEER#TOKEN=${localStorage.getItem("token")}`;
		copy(copyToken);
	}

	// 1. 创建隐藏的a标签
	const link = document.createElement("a");
	link.href = "seeronnet://main/home";
	link.style.display = "none";

	let appLaunched = false;

	// 3. 添加页面模糊事件监听（App打开成功会触发）
	const blurHandler = () => {
		appLaunched = true;
	};
	window.addEventListener("blur", blurHandler);

	// 4. 必须将元素添加到DOM才能工作
	document.body.appendChild(link);

	// 5. 模拟点击
	link.click();

	// 6. 设置超时检查
	setTimeout(() => {
		// 移除监听和元素
		window.removeEventListener("blur", blurHandler);
		document.body.removeChild(link);

		// 检查是否成功打开App
		if (!appLaunched && document.hasFocus()) {
			// 如果仍在当前页面，跳转下载页
			downloadApp(type);
		}
	}, 500);
}
